import { RandInt } from "./plateGame.ts";

/*
 * Factor Merge (ADR 0128): a 4 x 4 board of numbered tiles and three open
 * targets. A move takes one tile and merges it into another: the second becomes
 * the product, the first cell empties. A merge is allowed only if the product is
 * a factor of at least one open target's value; otherwise it is refused and
 * explained, and only the refused count moves. A tile that equals a target makes
 * it: tile and card clear, the score goes up one, a new target is dealt.
 *
 * Pure, like runnerGame.ts - no timers, no DOM, no React. Randomness comes in as
 * `randInt`, inclusive at both ends like `getRandomInt`, so tests can fix it.
 * Every function returns a new state and reads nothing but its arguments.
 *
 * The run ends when no allowed merge is left anywhere on the board (`over`).
 * The ADR words this "the board is full and no allowed merge exists". A board
 * that is not full but has no allowed merge is the same dead end - nothing can
 * spawn without a merge, and a clear cannot make a merge allowed - so it ends
 * the run too rather than leaving the child stuck. The check is made after every
 * move, a clear included.
 *
 * One level per grade, 3 to 8 (ADR 0128 §7). What a grade deals is data below:
 * its tile set, its target values and card kinds, how far the smallest target
 * steps up as targets are made, and what arrives after a merge (SPAWN_RULES).
 * New tiles are mostly a factor of an open target, picked from the grade's tile
 * set, so a tile is useful when it lands; at grades 3-5 one in two is any number
 * from the set.
 */

export type MergeLevel = "g3" | "g4" | "g5" | "g6" | "g7" | "g8";

export const MERGE_LEVELS: MergeLevel[] = ["g3", "g4", "g5", "g6", "g7", "g8"];

export const BOARD_SIDE = 4;
export const CELL_COUNT = BOARD_SIDE * BOARD_SIDE;
export const OPEN_TARGETS = 3;
/** Tiles a run starts with, unless the grade's SpawnRule says otherwise. */
export const START_TILES = 8;
/** Times a run can throw a tile away (ADR 0128 §4). */
export const CLEARS = 3;

const FACTS_2_TO_6 = [2, 3, 4, 5, 6];
const FACTS_2_TO_9 = [2, 3, 4, 5, 6, 7, 8, 9];
const PRIMES = [2, 3, 5, 7];

/** The numbers a tile can be when it arrives. A target's factors are drawn from these. */
export const TILE_SET: Record<MergeLevel, number[]> = {
    g3: FACTS_2_TO_6,
    g4: FACTS_2_TO_9,
    g5: FACTS_2_TO_9,
    g6: PRIMES,
    g7: PRIMES,
    g8: PRIMES,
};

/**
 * What arrives: a "factor" tile is a factor of a random open target, so it is
 * useful when it lands; an "any" tile is a number drawn evenly from the grade's
 * tile set, whatever the targets are, so some are dead on arrival.
 */
export type SpawnSlot = "factor" | "any";

export interface SpawnRule {
    /** Tiles the run starts with. */
    start: number;
    /** One tile for each slot after every allowed merge. */
    perMerge: SpawnSlot[];
    /** More, when that merge made a target. */
    perTarget: SpawnSlot[];
}

/**
 * New tiles after each allowed merge, and extra ones when that merge made a
 * target (ADR 0128 §2 table, §7). These are the numbers the ADR says to tune,
 * and each grade was tuned with greedy, random and mixed bots over 200 seeds
 * until the greedy median was 20 to 60 targets, the random median at least 5,
 * and at most 2% of runs reached a 2,000-move cap (the table is in the ADR).
 *
 * Grades 4 and 5: of the two tiles, the first is a factor of an open target and
 * the second is any number from the grade's tiles. Without the second, a careful
 * player never fills the board: every merge to a target is replaced by tiles
 * that fit one. Grades 3 and 6-8: one tile a merge that fits a target, and one
 * more when a target is made. At grade 3 the second "any" tile made the greedy
 * bot's median 65, past the limit, because the nine target values repeat so
 * often. Grades 7 and 8 start with 10 tiles, not 8: their targets need more
 * merges, and with 8 the greedy median fell to 21 and 18.
 */
export const SPAWN_RULES: Record<MergeLevel, SpawnRule> = {
    g3: { start: 8, perMerge: ["factor"], perTarget: ["factor"] },
    g4: { start: 8, perMerge: ["factor", "any"], perTarget: [] },
    g5: { start: 8, perMerge: ["factor", "any"], perTarget: [] },
    g6: { start: 8, perMerge: ["factor"], perTarget: ["factor"] },
    g7: { start: 10, perMerge: ["factor"], perTarget: ["factor"] },
    g8: { start: 10, perMerge: ["factor"], perTarget: ["factor"] },
};

/** The slots to fill after a merge: `made` is whether that merge made a target. */
export function spawnSlots(level: MergeLevel, made: boolean): SpawnSlot[] {
    const rule = SPAWN_RULES[level];
    return made ? [...rule.perMerge, ...rule.perTarget] : rule.perMerge;
}

/** Every this-many targets made, the smallest target dealt steps up to the next floor. */
export const TARGETS_PER_STEP = 10;

/**
 * The size of a grade's targets: the smallest value a target can have as the run
 * goes on (`floors`, one per step, the last held for good) and the largest. The
 * last floor is kept low enough that a pool is always left: with three cards open
 * and the one just made also barred, a pool needs more than three values.
 */
export const TARGET_RANGE: Record<MergeLevel, { floors: number[]; max: number }> = {
    g3: { floors: [12, 18], max: 36 },
    g4: { floors: [12, 24, 36, 48], max: 81 },
    g5: { floors: [24, 36, 48, 60], max: 144 },
    g6: { floors: [12, 24, 36, 48], max: 120 },
    g7: { floors: [30, 36, 42, 48], max: 210 },
    g8: { floors: [12, 24, 36, 48], max: 216 },
};

export type TargetKind = "make" | "lcm" | "lcm3" | "gcf" | "power";

/** A base and the exponent it is raised to: [2, 4] is 2 to the 4th. */
export type Power = [number, number];

/**
 * The cards a grade deals, as a roll of 1 to the length: grades 6 and 7 are half
 * "make", a quarter LCM and a quarter GCF (the LCM is of two numbers at 6, three
 * at 7); grades 3-5 are all "make" and grade 8 all exponent notation.
 */
const KIND_ROLLS: Record<MergeLevel, TargetKind[]> = {
    g3: ["make"],
    g4: ["make"],
    g5: ["make"],
    g6: ["make", "make", "lcm", "gcf"],
    g7: ["make", "make", "lcm3", "gcf"],
    g8: ["power"],
};

/** An LCM card's numbers are 2 to 12 with 11 left out (ADR 0128 §3, §7). */
const LCM_NUMBERS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 12];
/** A GCF card's two numbers are at most this, by grade. */
const GCF_MAX_NUMBER: Partial<Record<MergeLevel, number>> = { g6: 100, g7: 200 };

export interface MergeTarget {
    id: number;
    value: number;
    kind: TargetKind;
    /** The numbers an LCM or GCF card names, smallest first: two, or three on an "lcm3" card. */
    a?: number;
    b?: number;
    c?: number;
    /** What a "power" card shows: the product of these is the value, and one exponent is 2 or more. */
    powers?: Power[];
}

export interface MergeTile {
    id: number;
    value: number;
    /**
     * How the tile was made, sorted, for the line shown when it makes a target:
     * on grades 3-4 the two tiles last merged ("4 x 6"), from grade 5 on every
     * tile or prime that went in ("2 x 3 x 8", "2 x 2 x 3 x 5"). A fresh tile is
     * just itself.
     */
    parts: number[];
}

export type MergeEvent =
    | { kind: "start" }
    | { kind: "merged"; from: number; to: number; product: number }
    | { kind: "made"; from: number; to: number; product: number; parts: number[]; target: MergeTarget }
    /** `targets` are the open targets' values, in card order. */
    | { kind: "refused"; a: number; b: number; product: number; targets: number[] }
    | { kind: "cleared"; cell: number; value: number };

export interface MergeState {
    level: MergeLevel;
    cells: (MergeTile | null)[];
    targets: MergeTarget[];
    /** Targets made. */
    score: number;
    /** Merges refused. */
    refused: number;
    clearsLeft: number;
    /** Allowed merges made. */
    merges: number;
    /** No allowed merge is left. */
    over: boolean;
    lastEvent: MergeEvent;
    nextId: number;
}

export interface MergeResult {
    level: MergeLevel;
    targetsMade: number;
    refusedMerges: number;
}

export function resultOf(state: MergeState): MergeResult {
    return { level: state.level, targetsMade: state.score, refusedMerges: state.refused };
}

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

function lcm(a: number, b: number): number {
    return (a / gcd(a, b)) * b;
}

function pick<T>(items: T[], randInt: RandInt): T {
    return items[randInt(0, items.length - 1)];
}

// ── Exponent notation (grade 8) ─────────────────────────────────────────────

const SUPERSCRIPT = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function superscript(n: number): string {
    return String(n)
        .split("")
        .map((digit) => SUPERSCRIPT[Number(digit)])
        .join("");
}

/** "2⁴ × 3": a base with its exponent as a superscript when it is more than 1, the powers joined by ×. */
export function powerNotation(powers: Power[]): string {
    return powers.map(([base, exponent]) => (exponent === 1 ? String(base) : `${base}${superscript(exponent)}`)).join(" × ");
}

/** What a list of powers multiplies out to. */
export function powerValue(powers: Power[]): number {
    return powers.reduce((product, [base, exponent]) => product * base ** exponent, 1);
}

// ── Targets ─────────────────────────────────────────────────────────────────

/** The smallest target value dealt to this grade once `made` targets have been made. */
export function minTargetValue(level: MergeLevel, made: number): number {
    const { floors } = TARGET_RANGE[level];
    return floors[Math.min(floors.length - 1, Math.floor(made / TARGETS_PER_STEP))];
}

/** Products of `counts` numbers from `tiles`, between `min` and `max`. */
function productValues(tiles: number[], counts: number[], min: number, max: number): number[] {
    let layer = new Set<number>(tiles);
    const found = new Set<number>();
    for (let count = 2; count <= Math.max(...counts); count++) {
        const next = new Set<number>();
        for (const product of layer) {
            for (const tile of tiles) {
                next.add(product * tile);
            }
        }
        layer = next;
        if (counts.includes(count)) {
            for (const value of layer) {
                if (value >= min && value <= max) {
                    found.add(value);
                }
            }
        }
    }
    return [...found].sort((x, y) => x - y);
}

/** Numbers from `min` to `max` with no prime factor but 2, 3, 5 and 7. All are composite once min is 4 or more. */
function smoothValues(min: number, max: number): number[] {
    const values: number[] = [];
    for (let n = min; n <= max; n++) {
        let rest = n;
        for (const prime of PRIMES) {
            while (rest % prime === 0) {
                rest /= prime;
            }
        }
        if (rest === 1) {
            values.push(n);
        }
    }
    return values;
}

/** The first value a grade's targets can have; the floors only ever raise it. */
const firstFloor = (level: MergeLevel) => TARGET_RANGE[level].floors[0];

const MAKE_VALUES: Record<MergeLevel, number[]> = {
    g3: productValues(TILE_SET.g3, [2], firstFloor("g3"), TARGET_RANGE.g3.max),
    g4: productValues(TILE_SET.g4, [2], firstFloor("g4"), TARGET_RANGE.g4.max),
    g5: productValues(TILE_SET.g5, [2, 3], firstFloor("g5"), TARGET_RANGE.g5.max),
    g6: smoothValues(firstFloor("g6"), TARGET_RANGE.g6.max),
    g7: smoothValues(firstFloor("g7"), TARGET_RANGE.g7.max),
    g8: smoothValues(firstFloor("g8"), TARGET_RANGE.g8.max),
};

/** Is `value` a value this grade deals at all? */
function inRange(level: MergeLevel, value: number): boolean {
    return value >= firstFloor(level) && value <= TARGET_RANGE[level].max;
}

type NumberPool = Map<number, number[][]>;

function addTo<T>(pool: Map<number, T[]>, value: number, item: T) {
    pool.set(value, [...(pool.get(value) ?? []), item]);
}

/**
 * For each value, the numbers an LCM card can name: two (grade 6) or three
 * (grade 7) different numbers from LCM_NUMBERS, none a factor of another, whose
 * LCM is that value. "LCM of 4 and 12" is not a question, so such sets are left
 * out, and some values have none.
 */
function lcmPool(level: MergeLevel, size: 2 | 3): NumberPool {
    const pool: NumberPool = new Map();
    const indivisible = (x: number, y: number) => x % y !== 0 && y % x !== 0;
    for (const a of LCM_NUMBERS) {
        for (const b of LCM_NUMBERS) {
            if (a < b && indivisible(a, b)) {
                if (size === 2) {
                    const value = lcm(a, b);
                    if (inRange(level, value)) {
                        addTo(pool, value, [a, b]);
                    }
                    continue;
                }
                for (const c of LCM_NUMBERS) {
                    if (b < c && indivisible(a, c) && indivisible(b, c)) {
                        const value = lcm(lcm(a, b), c);
                        if (inRange(level, value)) {
                            addTo(pool, value, [a, b, c]);
                        }
                    }
                }
            }
        }
    }
    return pool;
}

/**
 * For each value, the pairs a GCF card can name: value x m and value x n for
 * coprime m < n, both at most the grade's limit. Only pairs where neither number
 * is the value itself (m of 2 or more) are kept, since "GCF of 24 and 48" answers
 * itself; values past a third of the limit have none, and get no GCF card.
 */
function gcfPool(level: MergeLevel, limit: number): NumberPool {
    const pool: NumberPool = new Map();
    for (const value of MAKE_VALUES[level]) {
        for (let m = 2; m * value <= limit; m++) {
            for (let n = m + 1; n * value <= limit; n++) {
                if (gcd(m, n) === 1) {
                    addTo(pool, value, [m * value, n * value]);
                }
            }
        }
    }
    return pool;
}

/** Composite bases that go into a card as themselves: "Make 6²", "Make 8²". */
const POWER_BASES = [4, 6, 8, 9, 10, 12, 14];

/**
 * For each value, the ways a grade 8 card can write it in exponent notation:
 * its prime factorization when some prime is repeated ("2⁴ × 3", "3³"), and a
 * composite base to a power when it is one ("6²", "10²"). Never a plain prime or
 * a product of different primes with no exponent.
 */
function powerPool(level: MergeLevel): Map<number, Power[][]> {
    const pool = new Map<number, Power[][]>();
    for (const value of MAKE_VALUES[level]) {
        const powers: Power[] = [];
        let rest = value;
        for (const prime of PRIMES) {
            let exponent = 0;
            while (rest % prime === 0) {
                rest /= prime;
                exponent++;
            }
            if (exponent > 0) {
                powers.push([prime, exponent]);
            }
        }
        if (powers.some(([, exponent]) => exponent >= 2)) {
            addTo(pool, value, powers);
        }
    }
    for (const base of POWER_BASES) {
        for (const exponent of [2, 3]) {
            const value = base ** exponent;
            if (inRange(level, value)) {
                const powers: Power[] = [[base, exponent]];
                addTo(pool, value, powers);
            }
        }
    }
    return pool;
}

interface CardPools {
    make: number[];
    lcm?: NumberPool;
    lcm3?: NumberPool;
    gcf?: NumberPool;
    power?: Map<number, Power[][]>;
}

const POOLS: Record<MergeLevel, CardPools> = {
    g3: { make: MAKE_VALUES.g3 },
    g4: { make: MAKE_VALUES.g4 },
    g5: { make: MAKE_VALUES.g5 },
    g6: { make: MAKE_VALUES.g6, lcm: lcmPool("g6", 2), gcf: gcfPool("g6", GCF_MAX_NUMBER.g6!) },
    g7: { make: MAKE_VALUES.g7, lcm3: lcmPool("g7", 3), gcf: gcfPool("g7", GCF_MAX_NUMBER.g7!) },
    g8: { make: MAKE_VALUES.g8, power: powerPool("g8") },
};

/** The values a card of this kind can have, in order. */
function valuesOf(level: MergeLevel, kind: TargetKind): number[] {
    const pools = POOLS[level];
    if (kind === "make") {
        return pools.make;
    }
    const pool = pools[kind];
    return pool ? [...pool.keys()].sort((x, y) => x - y) : [];
}

function valuePool(level: MergeLevel, kind: TargetKind, made: number, open: MergeTarget[]): number[] {
    const min = minTargetValue(level, made);
    const taken = new Set(open.map((target) => target.value));
    return valuesOf(level, kind).filter((value) => value >= min && !taken.has(value));
}

/**
 * Deals one target. The value is never one already open, so a made tile can only
 * ever clear one card; if the pool for the rolled kind is empty it falls back to
 * the grade's plain card ("make", or the notation at grade 8), and past that to a
 * repeat of an open value rather than to nothing.
 */
export function dealTarget(
    level: MergeLevel,
    made: number,
    open: MergeTarget[],
    id: number,
    randInt: RandInt
): MergeTarget {
    const rolls = KIND_ROLLS[level];
    const plain = rolls[0];
    let kind: TargetKind = rolls.length > 1 ? rolls[randInt(1, rolls.length) - 1] : plain;
    let pool = valuePool(level, kind, made, open);
    if (pool.length === 0 && kind !== plain) {
        kind = plain;
        pool = valuePool(level, kind, made, open);
    }
    if (pool.length === 0) {
        pool = valuePool(level, kind, made, []);
    }
    const value = pick(pool, randInt);
    const pools = POOLS[level];
    if (kind === "lcm" || kind === "lcm3" || kind === "gcf") {
        const [a, b, c] = pick(pools[kind]!.get(value)!, randInt);
        return c === undefined ? { id, value, kind, a, b } : { id, value, kind, a, b, c };
    }
    if (kind === "power") {
        return { id, value, kind, powers: pick(pools.power!.get(value)!, randInt) };
    }
    return { id, value, kind };
}

// ── The board ───────────────────────────────────────────────────────────────

/** Does a tile of this value divide at least one open target? */
export function isAllowedProduct(product: number, targets: MergeTarget[]): boolean {
    return targets.some((target) => target.value % product === 0);
}

/** Is any pair of tiles on the board one an allowed merge? The same for either order. */
export function hasAllowedMerge(cells: (MergeTile | null)[], targets: MergeTarget[]): boolean {
    const tiles = cells.filter((cell): cell is MergeTile => cell !== null);
    for (let i = 0; i < tiles.length; i++) {
        for (let j = i + 1; j < tiles.length; j++) {
            if (isAllowedProduct(tiles[i].value * tiles[j].value, targets)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Puts a new tile for each slot in a random empty cell and stops early if the
 * board fills. A "factor" tile is a factor of a random open target, drawn from
 * the grade's tile set; an "any" tile is any of the grade's tile set.
 */
function spawnTiles(
    cells: (MergeTile | null)[],
    targets: MergeTarget[],
    level: MergeLevel,
    slots: SpawnSlot[],
    nextId: number,
    randInt: RandInt
): { cells: (MergeTile | null)[]; nextId: number } {
    const next = [...cells];
    let id = nextId;
    for (const slot of slots) {
        const empty = next.flatMap((cell, index) => (cell === null ? [index] : []));
        if (empty.length === 0) {
            break;
        }
        let value: number;
        if (slot === "any") {
            value = pick(TILE_SET[level], randInt);
        } else {
            const target = pick(targets, randInt);
            value = pick(
                TILE_SET[level].filter((factor) => target.value % factor === 0),
                randInt
            );
        }
        next[pick(empty, randInt)] = { id: id++, value, parts: [value] };
    }
    return { cells: next, nextId: id };
}

/** Tries this many boards for one with a merge on it; a fresh board is dead only by freak chance. */
const START_ATTEMPTS = 50;

/** The opening tiles are all factors of the first targets, so the board starts useful. */
const startSlots = (level: MergeLevel): SpawnSlot[] => Array(SPAWN_RULES[level].start).fill("factor");

/** A fresh run: three targets and the grade's opening tiles, with at least one merge available. */
export function startGame(level: MergeLevel, randInt: RandInt): MergeState {
    let id = 1;
    const targets: MergeTarget[] = [];
    for (let i = 0; i < OPEN_TARGETS; i++) {
        targets.push(dealTarget(level, 0, targets, id++, randInt));
    }
    const deal = () => spawnTiles(Array(CELL_COUNT).fill(null), targets, level, startSlots(level), id, randInt);
    let board = deal();
    for (let attempt = 1; attempt < START_ATTEMPTS && !hasAllowedMerge(board.cells, targets); attempt++) {
        board = deal();
    }
    return {
        level,
        cells: board.cells,
        targets,
        score: 0,
        refused: 0,
        clearsLeft: CLEARS,
        merges: 0,
        over: !hasAllowedMerge(board.cells, targets),
        lastEvent: { kind: "start" },
        nextId: board.nextId,
    };
}

function sortedParts(parts: number[]): number[] {
    return [...parts].sort((x, y) => x - y);
}

/** "60 = 2 x 2 x 3 x 5", with × as a child reads it. */
export function factorizationLine(value: number, parts: number[]): string {
    return `${value} = ${parts.join(" × ")}`;
}

/**
 * The line shown when a target is made. A grade 8 card reads in its own notation
 * ("2⁴ × 3 = 48", "6² = 36"), however the tiles got there; every other card shows
 * what the tiles were.
 */
export function madeLine(target: MergeTarget, product: number, parts: number[]): string {
    return target.powers ? `${powerNotation(target.powers)} = ${product}` : factorizationLine(product, parts);
}

/**
 * Is a card at this grade just its number? At grades 3-5 it is. From grade 6 a
 * card says what to make ("Make 60", "LCM of 4 and 6", "Make 2⁴ × 3") and the
 * value is for the child to work out, so no message may name it: a refused merge
 * says "not a factor of any target" and never lists the values (ADR 0128 §7).
 */
export function cardShowsValue(level: MergeLevel): boolean {
    return level === "g3" || level === "g4" || level === "g5";
}

/** Grades 3 and 4 show the two tiles last merged; from grade 5 every tile that went in. */
function tracksEveryPart(level: MergeLevel): boolean {
    return level !== "g3" && level !== "g4";
}

/**
 * Merges the tile in `from` into the tile in `to`. Does nothing for an empty
 * cell, the same cell twice, or a finished run. A refused merge leaves the board
 * alone and counts a wrong move; an allowed one empties `from`, makes `to` the
 * product, and, if that product is a target, clears the tile and the card and
 * deals a new one. New tiles arrive last (SPAWN_RULES).
 */
export function mergeTiles(state: MergeState, from: number, to: number, randInt: RandInt): MergeState {
    const a = state.cells[from];
    const b = state.cells[to];
    if (state.over || from === to || !a || !b) {
        return state;
    }
    const product = a.value * b.value;

    if (!isAllowedProduct(product, state.targets)) {
        return {
            ...state,
            refused: state.refused + 1,
            lastEvent: {
                kind: "refused",
                a: a.value,
                b: b.value,
                product,
                targets: state.targets.map((target) => target.value),
            },
        };
    }

    const parts = sortedParts(tracksEveryPart(state.level) ? [...a.parts, ...b.parts] : [a.value, b.value]);
    let cells = [...state.cells];
    cells[from] = null;
    cells[to] = { id: state.nextId, value: product, parts };
    let nextId = state.nextId + 1;
    let targets = state.targets;
    let score = state.score;
    let event: MergeEvent = { kind: "merged", from, to, product };

    const hit = targets.findIndex((target) => target.value === product);
    if (hit !== -1) {
        const made = targets[hit];
        cells[to] = null;
        score += 1;
        targets = [...targets];
        targets[hit] = dealTarget(state.level, score, targets, nextId++, randInt);
        event = { kind: "made", from, to, product, parts, target: made };
    }

    const spawned = spawnTiles(cells, targets, state.level, spawnSlots(state.level, hit !== -1), nextId, randInt);
    cells = spawned.cells;
    nextId = spawned.nextId;

    return {
        ...state,
        cells,
        targets,
        score,
        merges: state.merges + 1,
        over: !hasAllowedMerge(cells, targets),
        lastEvent: event,
        nextId,
    };
}

/**
 * Throws a tile away, if a clear is left (ADR 0128 §4). No tile arrives after a
 * clear. A clear can end the run, by taking away the last pair that could merge.
 */
export function clearTile(state: MergeState, cell: number): MergeState {
    const tile = state.cells[cell];
    if (state.over || state.clearsLeft <= 0 || !tile) {
        return state;
    }
    const cells = [...state.cells];
    cells[cell] = null;
    return {
        ...state,
        cells,
        clearsLeft: state.clearsLeft - 1,
        over: !hasAllowedMerge(cells, state.targets),
        lastEvent: { kind: "cleared", cell, value: tile.value },
    };
}
